import React, { useState } from "react";
import { api, fmtErr } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { AvatarUpload } from "./AvatarUpload";
import { Hourglass, AlertCircle } from "lucide-react";

export function ProviderApplicationStatus({ user, refresh }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "", avatar: user?.avatar || "", location: user?.location || "", bio: user?.bio || "",
    services_offered: (user?.services_offered || user?.skills || []).join(", "),
    years_experience: user?.years_experience || 0, website: user?.website || "", instagram: user?.instagram || "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (user?.provider_status !== "rejected") {
    return (
      <div data-testid="provider-pending" className="max-w-2xl mx-auto px-6 py-20 text-center">
        <div className="w-16 h-16 rounded-2xl bg-amber-50 text-amber-700 flex items-center justify-center mx-auto"><Hourglass size={28} /></div>
        <h1 className="font-serif text-4xl mt-6">{t("pending_approval_title")}</h1>
        <p className="text-stone-600 mt-4">{t("pending_approval_msg")}</p>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/auth/provider-application", {
        ...form, avatar: form.avatar || null, website: form.website || null, instagram: form.instagram || null,
        years_experience: parseInt(form.years_experience) || 0,
        services_offered: form.services_offered.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success(t("resubmitted")); await refresh();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="provider-rejected" className="max-w-2xl mx-auto px-6 py-14">
      <div className="w-14 h-14 rounded-2xl bg-red-50 text-red-700 flex items-center justify-center"><AlertCircle size={26} /></div>
      <h1 className="font-serif text-4xl mt-5">{t("rejected_title")}</h1>
      <p className="text-stone-600 mt-3">{t("rejected_msg")}</p>
      {user.rejection_reason && (
        <div data-testid="rejection-reason" className="mt-5 bg-stone-50 border border-stone-200 rounded-2xl p-5">
          <div className="eyebrow text-stone-500 text-xs">{t("rejected_reason")}</div>
          <p className="mt-2 text-stone-800">{user.rejection_reason}</p>
        </div>
      )}
      <p className="text-sm text-stone-500 mt-6">{t("rejected_resubmit_hint")}</p>
      <form onSubmit={submit} className="mt-4 bg-white border border-stone-200 rounded-3xl p-6 space-y-4">
        <div><Label>{t("full_name")}</Label><Input data-testid="rs-name" value={form.name} onChange={set("name")} required /></div>
        <AvatarUpload value={form.avatar} onChange={(url) => setForm({ ...form, avatar: url })} testId="rs-avatar-upload" />
        <div><Label>{t("ps_location")}</Label><Input data-testid="rs-location" value={form.location} onChange={set("location")} /></div>
        <div><Label>{t("ps_services")}</Label><Input data-testid="rs-services" value={form.services_offered} onChange={set("services_offered")} placeholder={t("ps_services_ph")} /></div>
        <div><Label>{t("ps_bio")}</Label><Textarea data-testid="rs-bio" value={form.bio} onChange={set("bio")} rows={4} placeholder={t("ps_bio_ph")} /></div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>{t("ps_experience")}</Label><Input type="number" min="0" value={form.years_experience} onChange={set("years_experience")} /></div>
          <div><Label>{t("ps_website")}</Label><Input value={form.website} onChange={set("website")} /></div>
          <div><Label>{t("ps_instagram")}</Label><Input value={form.instagram} onChange={set("instagram")} /></div>
        </div>
        <Button data-testid="rs-submit" type="submit" disabled={busy} className="w-full h-11 rounded-full bg-stone-900 text-stone-50">{busy ? "..." : t("resubmit")}</Button>
      </form>
    </div>
  );
}
